import { RateData } from "src/types/rate-data";
import { ReactElement } from "react";
import dayjs from "dayjs";
import { formatCurrency } from "src/types/number";
import { useState, useEffect } from "react";
import { HiMiniFlag } from "react-icons/hi2";

export function HistoryTable({
    icon,
    data,
    loading,
    loadError,
    errorTitle,
    errorText,
    emptyTitle,
    emptyText,
    lane,
    avgAllIn,
    avgLineRev,
    avgFuelRev,
    avgRPM,
    avgFuelRate,
    avgBilledMiles,
}: {
    icon: ReactElement;
    data: RateData | null;
    loading: boolean;
    loadError: boolean;
    errorTitle: string;
    errorText: string;
    emptyTitle: string;
    emptyText: string;
    lane: string;
    avgAllIn: string;
    avgLineRev: string;
    avgFuelRev: string;
    avgRPM: string;
    avgFuelRate: string;
    avgBilledMiles: string;
}) {
    function addDays(date: Date, days: number) {
        let result = new Date(date);
        result.setDate(result.getDate() + days);
        return result;
    }

    const now = new Date();
    const thirtyDaysAgo = addDays(now, -30);
    const [averageLineHaul, setAverageLineHaul] = useState<number>(0);
    const [standardDeviation, setStandardDeviation] = useState<
        number | undefined
    >();

    useEffect(() => {
        // Calculate the standard deviation.
        var numRows = data?.rateHistoryAvgOfLane_PerCustomer.length ?? 1;
        var sumLineHaul = 0;
        data?.rateHistoryAvgOfLane_PerCustomer?.map((customer) => {
            sumLineHaul += customer.linehaul;
        });
        var avgLineHaul = sumLineHaul / numRows;
        setAverageLineHaul(avgLineHaul);

        var sumDifferences = 0;
        data?.rateHistoryAvgOfLane_PerCustomer?.map((customer) => {
            sumDifferences += (avgLineHaul - customer.linehaul) ** 2;
        });

        var numRows = data?.rateHistoryAvgOfLane_PerCustomer.length ?? 1;
        var avgDifference = sumDifferences / numRows;
        setStandardDeviation(Math.sqrt(avgDifference));
    }, [data?.rateHistoryAvgOfLane_PerCustomer, avgLineRev]);

    function getZScore(lineHaul: number) {
        return (lineHaul - averageLineHaul) / (standardDeviation ?? 1);
    }

    function isOutlier(lineHaul: number) {
        var zScore = getZScore(lineHaul);
        return zScore > 2 || zScore < -2;
    }

    return (
        <div className="card card-compact w-full h-full flex flex-col bg-gray-50 overflow-y-auto">
            <div
                className={`card-body py-0! px-0! gap-0! block! overflow-hidden h-full`}
            >
                <div className="h-full flex flex-col">
                    <div className="flex-1 overflow-auto">
                        {loading ? (
                            <div className="w-full h-full flex justify-center items-center flex-row">
                                <span className="loading loading-spinner loading-lg text-gray-600"></span>
                            </div>
                        ) : loadError ||
                          data?.rateHistoryAvgOfLane_PerCustomer == null ? (
                            <div className="w-full h-full flex justify-center items-center flex-col text-center">
                                <div className="flex flex-col items-center mb-8 w-lg">
                                    <div className="bg-gray-100 p-5 rounded-full mb-4">
                                        {icon}
                                    </div>
                                    <h2 className="text-xl font-bold text-gray-700 mb-2">
                                        {loadError ? errorTitle : emptyTitle}
                                    </h2>
                                    <p className="text-gray-600">
                                        {loadError ? errorText : emptyText}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <table className="table">
                                {/* head */}
                                <thead className="sticky top-0 bg-gray-50">
                                    <tr>
                                        <th className="w-xs p-0">
                                            <div className="flex flex-col justify-between">
                                                <div className="p-3">
                                                    <div className="flex flex-col">
                                                        <p>ProvidedLane</p>
                                                        <h2 className="text-2xl font-bold text-black">
                                                            {lane}
                                                        </h2>
                                                        <p className="italic">
                                                            {dayjs(
                                                                thirtyDaysAgo,
                                                            ).format("M/D/YY") +
                                                                " to " +
                                                                dayjs(
                                                                    now,
                                                                ).format(
                                                                    "M/D/YY",
                                                                )}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="p-3 bg-gray-200 italic">
                                                    Lane Average
                                                </div>
                                            </div>
                                        </th>
                                        <th className="align-bottom p-0">
                                            <div className="flex flex-col justify-between">
                                                <div className="p-3">
                                                    Avg All In
                                                </div>
                                                <div className="p-3 bg-gray-200 italic">
                                                    {avgAllIn}
                                                </div>
                                            </div>
                                        </th>
                                        <th className="align-bottom p-0">
                                            <div className="flex flex-col justify-between">
                                                <div className="p-3">
                                                    Avg Linehaul Revenue
                                                </div>
                                                <div className="p-3 bg-gray-200 italic">
                                                    {avgLineRev}
                                                </div>
                                            </div>
                                        </th>
                                        <th className="align-bottom p-0">
                                            <div className="flex flex-col justify-between">
                                                <div className="p-3">
                                                    Avg Fuel Revenue
                                                </div>
                                                <div className="p-3 bg-gray-200 italic">
                                                    {avgFuelRev}
                                                </div>
                                            </div>
                                        </th>
                                        <th className="align-bottom p-0">
                                            <div className="flex flex-col justify-between">
                                                <div className="p-3">
                                                    Avg All In RPM
                                                </div>
                                                <div className="p-3 bg-gray-200 italic">
                                                    {avgRPM}
                                                </div>
                                            </div>
                                        </th>
                                        <th className="align-bottom p-0">
                                            <div className="flex flex-col justify-between">
                                                <div className="p-3">
                                                    Avg Fuel Rate
                                                </div>
                                                <div className="p-3 bg-gray-200 italic">
                                                    {avgFuelRate}
                                                </div>
                                            </div>
                                        </th>
                                        <th className="align-bottom p-0">
                                            <div className="flex flex-col justify-between">
                                                <div className="p-3">
                                                    Avg Billed Miles
                                                </div>
                                                <div className="p-3 bg-gray-200 italic">
                                                    {avgBilledMiles}
                                                </div>
                                            </div>
                                        </th>
                                        <th className="align-bottom p-0 w-15">
                                            <div className="flex flex-col justify-between">
                                                <div className="p-3"></div>
                                                <div className="p-3 bg-gray-200 h-[45px]"></div>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <>
                                        {data?.rateHistoryAvgOfLane_PerCustomer?.map(
                                            (customer, id) => {
                                                return (
                                                    <tr key={id}>
                                                        <th className="min-w-3xs py-5">
                                                            {
                                                                customer.customer_name
                                                            }
                                                        </th>
                                                        <td>
                                                            {formatCurrency(
                                                                customer.linehaul +
                                                                    customer.fuel,
                                                            )}
                                                        </td>
                                                        <td>
                                                            {formatCurrency(
                                                                customer.linehaul,
                                                            )}
                                                        </td>
                                                        <td>
                                                            {formatCurrency(
                                                                customer.fuel,
                                                            )}
                                                        </td>
                                                        <td>
                                                            {formatCurrency(
                                                                customer.rpm,
                                                            )}
                                                        </td>
                                                        <td>
                                                            {formatCurrency(
                                                                customer.fuelRate,
                                                            )}
                                                        </td>
                                                        <td>
                                                            {customer.billed_miles.toFixed(
                                                                0,
                                                            )}
                                                        </td>
                                                        <td>
                                                            {isOutlier(
                                                                customer.linehaul,
                                                            ) ? (
                                                                <div className="w-full flex justify-center">
                                                                    <div
                                                                        className="tooltip tooltip-left"
                                                                        data-tip="Record is a statistical outlier"
                                                                    >
                                                                        <HiMiniFlag className="w-4 h-4 text-[#ac2226]" />
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <></>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            },
                                        )}
                                    </>
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
